import { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import {
    PlaygroundState,
    PlaygroundAction,
    PlaygroundContextType,
    PlaygroundParameters,
    PlaygroundMode,
    INITIAL_STATE,
    MAX_SELECTED_MODELS,
    MAX_USER_MESSAGES,
    ModelResponse,
    getResponseKey,
} from '../components/Playground/types';

const createInitialResponse = (): ModelResponse => ({
    content: '',
    isStreaming: false,
    isComplete: false,
    error: null,
    tokenCount: { input: 0, output: 0 },
    latencyMs: null,
    startTime: null,
});

function playgroundReducer(state: PlaygroundState, action: PlaygroundAction): PlaygroundState {
    switch (action.type) {
        case 'SET_MODE':
            return {
                ...state,
                mode: action.mode,
                responses: {},
                // Reset to single prompt/model when switching modes
                systemPrompts: [''],
                userMessages: [''],
                selectedModels: state.selectedModels.length > 0 ? [state.selectedModels[0]] : [],
            };

        case 'SET_AVAILABLE_MODELS':
            return { ...state, availableModels: action.models, modelPricing: action.pricing };

        case 'SET_SELECTED_MODELS':
            return {
                ...state,
                selectedModels: action.models.slice(0, MAX_SELECTED_MODELS),
            };

        case 'ADD_MODEL':
            if (state.selectedModels.length >= MAX_SELECTED_MODELS) {
                return state;
            }
            if (state.selectedModels.includes(action.modelId)) {
                return state;
            }
            return {
                ...state,
                selectedModels: [...state.selectedModels, action.modelId],
            };

        case 'REMOVE_MODEL':
            return {
                ...state,
                selectedModels: state.selectedModels.filter((m) => m !== action.modelId),
                responses: Object.fromEntries(
                    Object.entries(state.responses).filter(([key]) => key !== action.modelId)
                ),
            };

        case 'SET_SYSTEM_PROMPT': {
            const newSystemPrompts = [...state.systemPrompts];
            newSystemPrompts[action.index] = action.prompt;
            return { ...state, systemPrompts: newSystemPrompts };
        }

        case 'SET_USER_MESSAGE': {
            const newMessages = [...state.userMessages];
            newMessages[action.index] = action.message;
            return { ...state, userMessages: newMessages };
        }

        case 'ADD_PROMPT':
            if (state.userMessages.length >= MAX_USER_MESSAGES) {
                return state;
            }
            return {
                ...state,
                systemPrompts: [...state.systemPrompts, ''],
                userMessages: [...state.userMessages, ''],
            };

        case 'REMOVE_PROMPT':
            if (state.userMessages.length <= 1) {
                return state;
            }
            return {
                ...state,
                systemPrompts: state.systemPrompts.filter((_, i) => i !== action.index),
                userMessages: state.userMessages.filter((_, i) => i !== action.index),
            };

        case 'SET_PROMPTS':
            return {
                ...state,
                systemPrompts: action.systemPrompts,
                userMessages: action.userMessages,
            };

        case 'SET_PARAMETERS':
            return {
                ...state,
                parameters: { ...state.parameters, ...action.params },
            };

        case 'START_GENERATION': {
            const key = getResponseKey(action.promptIndex, action.modelId);
            return {
                ...state,
                responses: {
                    ...state.responses,
                    [key]: {
                        ...createInitialResponse(),
                        isStreaming: true,
                        startTime: Date.now(),
                    },
                },
            };
        }

        case 'APPEND_CONTENT': {
            const key = getResponseKey(action.promptIndex, action.modelId);
            const existingResponse = state.responses[key];
            if (!existingResponse) return state;
            return {
                ...state,
                responses: {
                    ...state.responses,
                    [key]: {
                        ...existingResponse,
                        content: existingResponse.content + action.content,
                    },
                },
            };
        }

        case 'COMPLETE_GENERATION': {
            const key = getResponseKey(action.promptIndex, action.modelId);
            const existingResponse = state.responses[key];
            if (!existingResponse) return state;
            const latencyMs = existingResponse.startTime
                ? Date.now() - existingResponse.startTime
                : null;
            return {
                ...state,
                responses: {
                    ...state.responses,
                    [key]: {
                        ...existingResponse,
                        isStreaming: false,
                        isComplete: true,
                        latencyMs,
                        tokenCount: action.tokenCount || existingResponse.tokenCount,
                    },
                },
            };
        }

        case 'GENERATION_ERROR': {
            const key = getResponseKey(action.promptIndex, action.modelId);
            const existingResponse = state.responses[key] || createInitialResponse();
            return {
                ...state,
                responses: {
                    ...state.responses,
                    [key]: {
                        ...existingResponse,
                        isStreaming: false,
                        isComplete: true,
                        error: action.error,
                    },
                },
            };
        }

        case 'LOAD_RESPONSE': {
            const key = getResponseKey(action.promptIndex, action.modelId);
            return {
                ...state,
                responses: {
                    ...state.responses,
                    [key]: {
                        content: action.content,
                        isStreaming: false,
                        isComplete: true,
                        error: null,
                        tokenCount: action.tokenCount,
                        latencyMs: null,
                        startTime: null,
                    },
                },
            };
        }

        case 'CLEAR_RESPONSES':
            return {
                ...state,
                responses: {},
                isGenerating: false,
            };

        case 'RESET_ALL':
            return {
                ...INITIAL_STATE,
                availableModels: state.availableModels,
                modelPricing: state.modelPricing,
            };

        case 'SET_IS_GENERATING':
            return { ...state, isGenerating: action.isGenerating };

        case 'TOGGLE_PARAMETERS_PANEL':
            return { ...state, isParametersPanelOpen: !state.isParametersPanelOpen };

        case 'LOAD_SESSION': {
            const { session } = action;
            const loadedResponses: Record<string, ModelResponse> = {};

            Object.entries(session.responses).forEach(([key, resp]) => {
                loadedResponses[key] = {
                    content: resp.content,
                    isStreaming: false,
                    isComplete: true,
                    error: null,
                    tokenCount: resp.tokenCount,
                    latencyMs: null,
                    startTime: null,
                };
            });

            return {
                ...state,
                mode: session.mode,
                selectedModels: session.selectedModels,
                systemPrompts: session.systemPrompts,
                userMessages: session.userMessages,
                parameters: session.parameters,
                responses: loadedResponses,
                isGenerating: false,
            };
        }

        default:
            return state;
    }
}

const PlaygroundContext = createContext<PlaygroundContextType | null>(null);

export function PlaygroundProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(playgroundReducer, INITIAL_STATE);

    const setMode = useCallback((mode: PlaygroundMode) => {
        dispatch({ type: 'SET_MODE', mode });
    }, []);

    const setSelectedModels = useCallback((models: string[]) => {
        dispatch({ type: 'SET_SELECTED_MODELS', models });
    }, []);

    const addModel = useCallback((modelId: string) => {
        dispatch({ type: 'ADD_MODEL', modelId });
    }, []);

    const removeModel = useCallback((modelId: string) => {
        dispatch({ type: 'REMOVE_MODEL', modelId });
    }, []);

    const setSystemPrompt = useCallback((index: number, prompt: string) => {
        dispatch({ type: 'SET_SYSTEM_PROMPT', index, prompt });
    }, []);

    const setUserMessage = useCallback((index: number, message: string) => {
        dispatch({ type: 'SET_USER_MESSAGE', index, message });
    }, []);

    const addPrompt = useCallback(() => {
        dispatch({ type: 'ADD_PROMPT' });
    }, []);

    const removePrompt = useCallback((index: number) => {
        dispatch({ type: 'REMOVE_PROMPT', index });
    }, []);

    const setPrompts = useCallback((systemPrompts: string[], userMessages: string[]) => {
        dispatch({ type: 'SET_PROMPTS', systemPrompts, userMessages });
    }, []);

    const setParameters = useCallback((params: Partial<PlaygroundParameters>) => {
        dispatch({ type: 'SET_PARAMETERS', params });
    }, []);

    const clearResponses = useCallback(() => {
        dispatch({ type: 'CLEAR_RESPONSES' });
    }, []);

    const resetAll = useCallback(() => {
        dispatch({ type: 'RESET_ALL' });
    }, []);

    const toggleParametersPanel = useCallback(() => {
        dispatch({ type: 'TOGGLE_PARAMETERS_PANEL' });
    }, []);

    const value: PlaygroundContextType = {
        state,
        dispatch,
        setMode,
        setSelectedModels,
        addModel,
        removeModel,
        setSystemPrompt,
        setUserMessage,
        addPrompt,
        removePrompt,
        setPrompts,
        setParameters,
        clearResponses,
        resetAll,
        toggleParametersPanel,
    };

    return (
        <PlaygroundContext.Provider value={value}>
            {children}
        </PlaygroundContext.Provider>
    );
}

export function usePlayground(): PlaygroundContextType {
    const context = useContext(PlaygroundContext);
    if (!context) {
        throw new Error('usePlayground must be used within a PlaygroundProvider');
    }
    return context;
}

export { PlaygroundContext };
